import React, { useState } from 'react';
import { NimbusModal } from './NimbusModal';
import Select from 'react-select';
import type { Person } from '../types/models';
import { NimbusTable } from './NimbusTable';

interface RaciAssignment {
  personId: string;
  personName: string;
  role: 'R' | 'A' | 'C' | 'I';
}

interface Requirement {
  requirementId: string;
  text: string;
  status: 'Planned' | 'In Progress' | 'Complete' | 'Blocked';
  raci: RaciAssignment[];
}

interface RequirementsTrackerProps {
  entityType: string;
  entityId: string;
  functionalRequirements: Requirement[];
  nonFunctionalRequirements: Requirement[];
  onUpdate: (functional: Requirement[], nonFunctional: Requirement[]) => void;
  people: Person[];
}

const statusOptions = [
  { value: 'Planned', label: 'Planned' },
  { value: 'In Progress', label: 'In Progress' },
  { value: 'Complete', label: 'Complete' },
  { value: 'Blocked', label: 'Blocked' },
];

export const RequirementsTracker: React.FC<RequirementsTrackerProps> = ({
  functionalRequirements = [],
  nonFunctionalRequirements = [],
  onUpdate,
  people = [],
}) => {
  const [newFunctional, setNewFunctional] = useState('');
  const [newNonFunctional, setNewNonFunctional] = useState('');
  const [editingRaci, setEditingRaci] = useState<{
    requirement: Requirement;
    isFunctional: boolean;
  } | null>(null);

  const generateId = () => `REQ-${Math.random().toString(36).substr(2, 8)}`;

  const calculateCompletion = (requirements: Requirement[]) => {
    if (requirements.length === 0) return 0;
    const completed = requirements.filter((r) => r.status === 'Complete').length;
    return Math.round((completed / requirements.length) * 100);
  };

  const handleAddFunctional = () => {
    if (!newFunctional.trim()) return;
    const newReq: Requirement = {
      requirementId: generateId(),
      text: newFunctional,
      status: 'Planned',
      raci: [],
    };
    onUpdate([...functionalRequirements, newReq], nonFunctionalRequirements);
    setNewFunctional('');
  };

  const handleAddNonFunctional = () => {
    if (!newNonFunctional.trim()) return;
    const newReq: Requirement = {
      requirementId: generateId(),
      text: newNonFunctional,
      status: 'Planned',
      raci: [],
    };
    onUpdate(functionalRequirements, [...nonFunctionalRequirements, newReq]);
    setNewNonFunctional('');
  };

  const handleDeleteFunctional = (id: string) => {
    onUpdate(
      functionalRequirements.filter((r) => r.requirementId !== id),
      nonFunctionalRequirements
    );
  };

  const handleDeleteNonFunctional = (id: string) => {
    onUpdate(
      functionalRequirements,
      nonFunctionalRequirements.filter((r) => r.requirementId !== id)
    );
  };

  const handleStatusChange = (
    id: string,
    status: string,
    isFunctional: boolean
  ) => {
    if (isFunctional) {
      onUpdate(
        functionalRequirements.map((r) =>
          r.requirementId === id ? { ...r, status: status as Requirement['status'] } : r
        ),
        nonFunctionalRequirements
      );
    } else {
      onUpdate(
        functionalRequirements,
        nonFunctionalRequirements.map((r) =>
          r.requirementId === id ? { ...r, status: status as Requirement['status'] } : r
        )
      );
    }
  };

  const handleRaciChange = (
    role: 'R' | 'A' | 'C' | 'I',
    selectedOptions: any
  ) => {
    if (!editingRaci) return;
    
    const updateRaci = (req: Requirement) => {
      if (req.requirementId !== editingRaci.requirement.requirementId) return req;
      
      // Remove all existing assignments for this role
      const otherRoles = req.raci.filter((r) => r.role !== role);
      
      // Add new assignments for this role
      const newAssignments = selectedOptions
        ? (Array.isArray(selectedOptions) ? selectedOptions : [selectedOptions]).map((opt: any) => ({
            personId: opt.value,
            personName: opt.label,
            role,
          }))
        : [];
      
      const updatedReq = {
        ...req,
        raci: [...otherRoles, ...newAssignments],
      };
      
      // Update the modal state too
      setEditingRaci({ ...editingRaci, requirement: updatedReq });
      
      return updatedReq;
    };

    if (editingRaci.isFunctional) {
      onUpdate(functionalRequirements.map(updateRaci), nonFunctionalRequirements);
    } else {
      onUpdate(functionalRequirements, nonFunctionalRequirements.map(updateRaci));
    }
  };

  const getRaciByRole = (requirement: Requirement, role: 'R' | 'A' | 'C' | 'I') => {
    return requirement.raci
      .filter((r) => r.role === role)
      .map((r) => ({ value: r.personId, label: r.personName }));
  };

  const getRoleColor = (role: 'R' | 'A' | 'C' | 'I'): string => {
    const colors = {
      R: 'primary',
      A: 'success',
      C: 'info',
      I: 'warning',
    };
    return colors[role];
  };

  const getInitials = (name: string): string => {
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  };

  const renderRequirementRow = (req: Requirement, isFunctional: boolean) => {
    const getPersonForRole = (role: 'R' | 'A' | 'C' | 'I'): string => {
      const assignment = req.raci?.find((a) => a.role === role);
      return assignment?.personName || '—';
    };
    
    return (
      <tr key={req.requirementId}>
        <td style={{ width: '120px' }} className="align-middle text-muted small">{getPersonForRole('R')}</td>
        <td style={{ width: '120px' }} className="align-middle text-muted small">{getPersonForRole('A')}</td>
        <td style={{ width: '120px' }} className="align-middle text-muted small">{getPersonForRole('C')}</td>
        <td style={{ width: '120px' }} className="align-middle text-muted small">{getPersonForRole('I')}</td>
        <td className="align-middle">{req.text}</td>
        <td style={{ width: '150px' }}>
          <Select
            options={statusOptions}
            value={statusOptions.find((opt) => opt.value === req.status)}
            onChange={(option) =>
              option && handleStatusChange(req.requirementId, option.value, isFunctional)
            }
            menuPortalTarget={document.body}
            menuPosition="fixed"
            styles={{
              control: (base) => ({
                ...base,
                minHeight: '32px',
                fontSize: '0.875rem',
              }),
              menuPortal: (base) => ({ ...base, zIndex: 200000 }),
            }}
          />
        </td>
        <td style={{ width: '120px' }} className="text-center">
          <div className="d-flex gap-1 justify-content-center">
            <button
              className={`np-icon-btn${req.raci && req.raci.length > 0 ? ' np-tone-primary' : ''}`}
              onClick={() => setEditingRaci({ requirement: req, isFunctional })}
              title="Manage RACI assignments"
            >
              <i className="ri-team-line"></i>
            </button>
            <button
              className="np-icon-btn np-icon-btn-danger"
              onClick={() =>
                isFunctional
                  ? handleDeleteFunctional(req.requirementId)
                  : handleDeleteNonFunctional(req.requirementId)
              }
              title="Delete requirement"
            >
              <i className="ri-delete-bin-line"></i>
            </button>
          </div>
        </td>
      </tr>
    );
  };

  const functionalCompletion = calculateCompletion(functionalRequirements);
  const nonFunctionalCompletion = calculateCompletion(nonFunctionalRequirements);

  const personOptions = people.map((p) => ({
    value: p.personId,
    label: p.personName,
  }));

  return (
    <>
      {/* RACI Edit Modal - rendered via portal to escape navbar stacking context */}
      {editingRaci && (
        <NimbusModal size="lg" onDialogClick={(e) => e.stopPropagation()}>
          <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="ri-team-line me-2"></i>
                  RACI Assignments
                </h5>
                <button type="button" className="btn-close" onClick={() => setEditingRaci(null)}></button>
              </div>
              <div className="modal-body">
                <div className="np-field">
                  <label>Requirement</label>
                  <p className="mb-0">{editingRaci.requirement.text}</p>
                </div>

                <div className="row g-3">
                  <div className="col-md-6 np-field">
                    <label>
                      <span className={`np-chip np-tone-${getRoleColor('R')} me-2`}>R</span>
                      <strong>Responsible</strong>
                    </label>
                    <Select
                      isMulti
                      options={personOptions}
                      value={getRaciByRole(editingRaci.requirement, 'R')}
                      onChange={(selected) => handleRaciChange('R', selected)}
                      placeholder="Select people..."
                      isClearable
                      isSearchable
                    />
                    <p className="np-hint">Who does the work to complete the task</p>
                  </div>

                  <div className="col-md-6 np-field">
                    <label>
                      <span className={`np-chip np-tone-${getRoleColor('A')} me-2`}>A</span>
                      <strong>Accountable</strong>
                    </label>
                    <Select
                      options={personOptions}
                      value={getRaciByRole(editingRaci.requirement, 'A')[0] || null}
                      onChange={(selected) => handleRaciChange('A', selected)}
                      placeholder="Select person..."
                      isClearable
                      isSearchable
                    />
                    <p className="np-hint">Who is ultimately answerable (one person only)</p>
                  </div>

                  <div className="col-md-6 np-field">
                    <label>
                      <span className={`np-chip np-tone-${getRoleColor('C')} me-2`}>C</span>
                      <strong>Consulted</strong>
                    </label>
                    <Select
                      isMulti
                      options={personOptions}
                      value={getRaciByRole(editingRaci.requirement, 'C')}
                      onChange={(selected) => handleRaciChange('C', selected)}
                      placeholder="Select people..."
                      isClearable
                      isSearchable
                    />
                    <p className="np-hint">Who provides input and feedback</p>
                  </div>

                  <div className="col-md-6 np-field">
                    <label>
                      <span className={`np-chip np-tone-${getRoleColor('I')} me-2`}>I</span>
                      <strong>Informed</strong>
                    </label>
                    <Select
                      isMulti
                      options={personOptions}
                      value={getRaciByRole(editingRaci.requirement, 'I')}
                      onChange={(selected) => handleRaciChange('I', selected)}
                      placeholder="Select people..."
                      isClearable
                      isSearchable
                    />
                    <p className="np-hint">Who is kept up-to-date on progress</p>
                  </div>
                </div>

                {editingRaci.requirement.raci && editingRaci.requirement.raci.length > 0 && (
                  <div className="mt-4">
                    <h6 className="mb-3">Current Assignments</h6>
                    <NimbusTable
                      resizable={false}
                      thead={
                        <tr>
                          <th>Person</th>
                          <th>Role</th>
                        </tr>
                      }
                    >
                      {editingRaci.requirement.raci.map((assignment, idx) => (
                        <tr key={`${assignment.personId}-${assignment.role}-${idx}`}>
                          <td>
                            <div className="d-flex align-items-center">
                              <div className="avatar avatar-xs me-2">
                                <span className={`avatar-initial rounded-circle bg-label-${getRoleColor(assignment.role)}`} style={{ fontSize: '0.65rem' }}>
                                  {getInitials(assignment.personName)}
                                </span>
                              </div>
                              {assignment.personName}
                            </div>
                          </td>
                          <td>
                            <span className={`np-chip np-tone-${getRoleColor(assignment.role)}`}>
                              {assignment.role}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </NimbusTable>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="np-btn np-btn-ghost" onClick={() => setEditingRaci(null)}>
                  Close
                </button>
              </div>
            </div>
        </NimbusModal>
      )}

      <div className="col-12 mb-4">
      <div className="np-card">
        <div className="np-card-head">
          <h2><i className="ri-task-line"></i> Functional & Non-Functional Requirements</h2>
        </div>
        <div className="np-card-body">
          {/* Functional Requirements */}
          <div className="mb-4">
            <h6 className="mb-3">Functional Requirements</h6>
            <div className="input-group mb-3">
              <input
                type="text"
                className="np-input"
                placeholder="Enter functional requirement..."
                value={newFunctional}
                onChange={(e) => setNewFunctional(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddFunctional()}
              />
              <button className="np-btn np-btn-primary" onClick={handleAddFunctional}>
                <i className="ri-add-line"></i>Add
              </button>
            </div>

            {functionalRequirements.length > 0 && (
              <>
                <NimbusTable thead={<tr>
                        <th style={{ width: '120px' }}>RESPONSIBLE</th>
                        <th style={{ width: '120px' }}>ACCOUNTABLE</th>
                        <th style={{ width: '120px' }}>CONSULTED</th>
                        <th style={{ width: '120px' }}>INFORMED</th>
                        <th>REQUIREMENT</th>
                        <th style={{ width: '150px' }}>STATUS</th>
                        <th style={{ width: '120px' }} className="text-center">ACTIONS</th>
                      </tr>}>
                      {functionalRequirements.map((req) => renderRequirementRow(req, true))}
                </NimbusTable>
                <div className="d-flex justify-content-end align-items-center mt-2">
                  <strong className="me-2">% Completion:</strong>
                  <span
                    className={`np-chip np-tabular np-tone-${
                      functionalCompletion === 100
                        ? 'success'
                        : functionalCompletion > 0
                        ? 'warning'
                        : 'danger'
                    }`}
                  >
                    {functionalCompletion}%
                  </span>
                </div>
              </>
            )}
            <p className="np-sub">Track functional requirements and their completion status</p>
          </div>

          {/* Non-Functional Requirements */}
          <div className="mb-4">
            <h6 className="mb-3">Non-Functional Requirements</h6>
            <div className="input-group mb-3">
              <input
                type="text"
                className="np-input"
                placeholder="Enter non-functional requirement..."
                value={newNonFunctional}
                onChange={(e) => setNewNonFunctional(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddNonFunctional()}
              />
              <button className="np-btn np-btn-primary" onClick={handleAddNonFunctional}>
                <i className="ri-add-line"></i>Add
              </button>
            </div>

            {nonFunctionalRequirements.length > 0 && (
              <>
                <NimbusTable thead={<tr>
                        <th style={{ width: '120px' }}>RESPONSIBLE</th>
                        <th style={{ width: '120px' }}>ACCOUNTABLE</th>
                        <th style={{ width: '120px' }}>CONSULTED</th>
                        <th style={{ width: '120px' }}>INFORMED</th>
                        <th>REQUIREMENT</th>
                        <th style={{ width: '150px' }}>STATUS</th>
                        <th style={{ width: '120px' }} className="text-center">ACTIONS</th>
                      </tr>}>
                      {nonFunctionalRequirements.map((req) => renderRequirementRow(req, false))}
                </NimbusTable>
                <div className="d-flex justify-content-end align-items-center mt-2">
                  <strong className="me-2">% Completion:</strong>
                  <span
                    className={`np-chip np-tabular np-tone-${
                      nonFunctionalCompletion === 100
                        ? 'success'
                        : nonFunctionalCompletion > 0
                        ? 'warning'
                        : 'danger'
                    }`}
                  >
                    {nonFunctionalCompletion}%
                  </span>
                </div>
              </>
            )}
            <p className="np-sub">Track non-functional requirements and their completion status</p>
          </div>

          {/* Completion Summary */}
          <div className="np-grid np-grid-2">
            <div className="np-card">
              <div className="np-card-body">
                <div className="np-stat">
                  <span className="np-stat-icon np-tone-primary"><i className="ri-list-check-2"></i></span>
                  <p className="np-stat-label">Functional Requirements Completion</p>
                  <p className="np-stat-value np-tabular">{functionalCompletion}%</p>
                </div>
                <p className="np-hint mb-0">Auto-calculated based on requirement status</p>
              </div>
            </div>
            <div className="np-card">
              <div className="np-card-body">
                <div className="np-stat">
                  <span className="np-stat-icon np-tone-info"><i className="ri-shield-check-line"></i></span>
                  <p className="np-stat-label">Non-Functional Requirements Completion</p>
                  <p className="np-stat-value np-tabular">{nonFunctionalCompletion}%</p>
                </div>
                <p className="np-hint mb-0">Auto-calculated based on requirement status</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};
